'use client';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import { IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import {
  StyledValuesBanner,
  ScrollContainer,
  MarqueeOne,
  MarqueeTwo,
  ValuesWrapper,
  ValueList,
  Value,
  IconWrapper,
  Text,
} from './styles.js';

const VALUE_ICON_SIZE = 24;

export const getMarqueeRepeatCount = (containerWidth, copyWidth) => {
  if (!copyWidth) {
    return 1;
  }
  return Math.max(1, Math.ceil(containerWidth / copyWidth));
};

export const ValuesBanner = ({
  values,
  backgroundColor = 'background.dark',
}) => {
  const containerRef = useRef(null);
  const copyRef = useRef(null);

  const [repeat, setRepeat] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClickPaused, setIsClickPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isAnimationPaused = isHovered || isClickPaused || prefersReducedMotion;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = e => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isClickPaused) return;

    const resume = () => setIsClickPaused(false);

    // Defer so the click/tap that paused does not immediately resume.
    const frameId = requestAnimationFrame(() => {
      document.addEventListener('click', resume);
      document.addEventListener('touchstart', resume);
    });

    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener('click', resume);
      document.removeEventListener('touchstart', resume);
    };
  }, [isClickPaused]);

  const handlePauseInteraction = e => {
    e.stopPropagation();
    setIsClickPaused(true);
  };

  useEffect(() => {
    let cancelled = false;
    let fadeTimeout;

    const updateRepeat = () => {
      if (cancelled) return;
      const copyEl = copyRef.current;
      const containerEl = containerRef.current;
      if (!copyEl || !containerEl) return;
      const copyWidth = copyEl.getBoundingClientRect().width;
      if (!copyWidth) return;
      const containerWidth = containerEl.getBoundingClientRect().width;
      setRepeat(getMarqueeRepeatCount(containerWidth, copyWidth));
      clearTimeout(fadeTimeout);
      fadeTimeout = setTimeout(() => {
        if (!cancelled) setLoaded(true);
      }, 250);
    };

    updateRepeat();
    window.addEventListener('resize', updateRepeat);
    const observer = new ResizeObserver(updateRepeat);
    if (copyRef.current) observer.observe(copyRef.current);
    if (containerRef.current) observer.observe(containerRef.current);
    document.fonts?.ready?.then(updateRepeat);

    return () => {
      cancelled = true;
      clearTimeout(fadeTimeout);
      window.removeEventListener('resize', updateRepeat);
      observer.disconnect();
    };
  }, [values]);

  const renderCopies = (firstCopyRef = null) =>
    range(repeat).map(i => (
      <ValuesWrapper key={i} ref={i === 0 ? firstCopyRef : undefined}>
        <ValueList>
          {values.map(({ image, name, fontColor }) => (
            <Value key={name} fontColor={fontColor || 'text.lightest'}>
              {image && (
                <IconWrapper>
                  <ResponsiveImage
                    src={image.src}
                    alt={image.alt}
                    width={VALUE_ICON_SIZE}
                    height={VALUE_ICON_SIZE}
                    objectFit="contain"
                    sizes={IMAGE_SIZES.fixed(VALUE_ICON_SIZE)}
                    priority
                  />
                </IconWrapper>
              )}
              <Text>{name}</Text>
            </Value>
          ))}
        </ValueList>
      </ValuesWrapper>
    ));

  return (
    <StyledValuesBanner
      ref={containerRef}
      backgroundColor={backgroundColor}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handlePauseInteraction}
      onTouchStart={handlePauseInteraction}
    >
      <ScrollContainer>
        <MarqueeOne $loaded={loaded} $paused={isAnimationPaused}>
          {renderCopies(copyRef)}
        </MarqueeOne>
        <MarqueeTwo $loaded={loaded} $paused={isAnimationPaused}>
          {renderCopies()}
        </MarqueeTwo>
      </ScrollContainer>
    </StyledValuesBanner>
  );
};

ValuesBanner.displayName = 'ValuesBanner';

ValuesBanner.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.shape({
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
          .isRequired,
        alt: PropTypes.string.isRequired,
        fontColor: PropTypes.string,
      }),
    }),
  ).isRequired,
};
